/*
 *
 * AdminPage / Users / UsersList
 *
 * Paginated list of all users. Backed by GET /api/v3/admin/users, which returns
 * a pagination envelope { current_page, rows_per_page, page_count, data: [{ id,
 * first_name, last_name, email }] } — the list rows carry NO permission_role
 * (that's detail-only), so the table shows name + email and links each row to
 * the detail screen where the role is available.
 *
 * The endpoint accepts only page/per_page — the `search`/`role` query params
 * were phantom (documented but never implemented server-side, removed from the
 * spec), so this is pagination-only to match the rails superadmin parity. Server
 * -side search/role filtering is a deferred enhancement, not retirement parity.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import Table from "components/Table";
import Loader from "components/Loader";
import { useAdminUsers } from "../api/queries";
import { Wrapper, Toolbar, NewButton } from "./styledComponents";

// Columns mirror the ADMIN_INDEX_USER_SHAPE row fields. Sorting is disabled —
// the backend owns ordering/pagination, so client-side sort of a single page
// would be misleading.
const columns = [
  { header: "First", path: "first_name", width: "160px", noSort: true,
    customRow: (row) => row.first_name || "—" },
  { header: "Last", path: "last_name", width: "160px", noSort: true,
    customRow: (row) => row.last_name || "—" },
  { header: "Email", path: "email", width: "280px", noSort: true,
    customRow: (row) => row.email },
];

const UsersList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, isFetching } = useAdminUsers({ page });

  const rows = data?.data || [];

  return (
    <Wrapper>
      <Toolbar>
        <NewButton type="button" onClick={() => navigate("/admin/users/new")}>
          New user
        </NewButton>
      </Toolbar>

      {isLoading ? (
        <div data-testid="users-loading">
          <Loader />
        </div>
      ) : isError ? (
        <p className="admin-users__error">Couldn’t load users. Try again.</p>
      ) : rows.length === 0 ? (
        <p className="admin-users__empty">No users to show.</p>
      ) : (
        <Table
          columns={columns}
          data={rows}
          isFetching={isFetching}
          currentPage={data?.current_page || page}
          pageCount={data?.page_count || 1}
          onPageChange={setPage}
          onRowClick={(row) => navigate(`/admin/users/${row.id}`)}
        />
      )}
    </Wrapper>
  );
};

export default UsersList;
